/**
 * Polling utility for Step Function status checking
 * Sử dụng đệ quy thay vì setInterval để tránh memory leak
 */

export interface PollingOptions {
  /** Delay for first 5 attempts (ms) */
  initialDelay?: number;
  /** Delay for subsequent attempts (ms) */
  subsequentDelay?: number;
  /** Maximum number of attempts (0 = unlimited) */
  maxAttempts?: number;
  /** Number of attempts to use initial delay */
  initialDelayAttempts?: number;
}

export interface PollingCallbacks<T, R = void> {
  /** Callback when polling succeeds - can return a value */
  onSuccess: (data: T) => R | Promise<R>;
  /** Callback when polling fails or times out */
  onError?: (error?: any) => R | undefined | Promise<R | undefined>;
  /** Check function to determine if should continue polling */
  shouldContinue: (response: any) => boolean;
  /** Extract data from response */
  extractData?: (response: any) => T;
}

/**
 * Generic polling function using recursion
 * @param checkStatusFn Function to check status (returns Promise)
 * @param callbacks Callbacks for success/error/continue
 * @param options Polling options
 * @param attemptCount Current attempt count (internal use)
 * @returns Promise that resolves with the return value from onSuccess callback
 */
export function pollWithRecursion<T, R = void>(
  checkStatusFn: () => Promise<any>,
  callbacks: PollingCallbacks<T, R>,
  options: PollingOptions = {},
  attemptCount: number = 0
): Promise<R | undefined> {
  const {
    initialDelay = 1000,
    subsequentDelay = 2000,
    maxAttempts = 0,
    initialDelayAttempts = 5
  } = options;

  return new Promise((resolve) => {
    // Check max attempts
    if (maxAttempts > 0 && attemptCount >= maxAttempts) {
      if (callbacks.onError) {
        const result = callbacks.onError(new Error(`Max attempts (${maxAttempts}) reached`));
        resolve(result instanceof Promise ? result : result as R | undefined);
      } else {
        resolve(undefined);
      }
      return;
    }

    // Calculate delay
    const delay = attemptCount < initialDelayAttempts ? initialDelay : subsequentDelay;

    setTimeout(async () => {
      try {
        const response = await checkStatusFn();

        // Check if should continue polling
        if (callbacks.shouldContinue(response)) {
          // Continue polling
          const result = await pollWithRecursion(checkStatusFn, callbacks, options, attemptCount + 1);
          resolve(result);
          return;
        }

        // Extract and return data
        const data = callbacks.extractData ? callbacks.extractData(response) : response;
        const result = await callbacks.onSuccess(data);
        resolve(result);
      } catch (error) {
        console.error('Error during polling:', error);
        if (callbacks.onError) {
          const result = await callbacks.onError(error);
          resolve(result);
        } else {
          resolve(undefined);
        }
      }
    }, delay);
  });
}

/**
 * Polling specifically for Step Function Status
 * Wrapper around pollWithRecursion with Step Function specific logic
 * @returns Promise that resolves with the return value from onSuccess callback
 */
export async function pollStepFunctionStatus<T, R = void>(
  stepFunctionService: any,
  executionArn: string,
  callbacks: {
    onSuccess: (data: T) => R | Promise<R>;
    onError?: (error?: any) => R | Promise<R>;
    onFinally?: (data?: T) => void;
  },
  options: PollingOptions = {}
): Promise<R | undefined> {
  let pivotData: any = null; // Store pivot data for cleanup

  return await pollWithRecursion<T, R>(
    async () => await stepFunctionService.checkStatus(executionArn),
    {
      onSuccess: async (data: T): Promise<R> => {
        try {
          return await callbacks.onSuccess(data);
        } finally {
          if (callbacks.onFinally) {
            callbacks.onFinally(pivotData || data);
          }
        }
      },
      onError: async (error?: any): Promise<R | undefined> => {
        try {
          if (callbacks.onError) {
            return await callbacks.onError(error);
          }
          return undefined;
        } finally {
          if (callbacks.onFinally) {
            callbacks.onFinally(pivotData);
          }
        }
      },
      shouldContinue: (response: any) => {
        // Extract pivot data for cleanup regardless of final status
        if (response?.data?.output) {
          try {
            const parsed = JSON.parse(response.data.output);
            if (parsed?.pivotId) {
              pivotData = parsed;
            }
          } catch (e) {
            // Ignore parsing errors
          }
        }

        // Continue if status is RUNNING
        if (!response?.data?.status) {
          return false; // Stop if no status
        }
        return response.data.status === 'RUNNING';
      },
      extractData: (response: any) => {
        if (!response?.data) {
          return response;
        }
        if (response.data.status === 'SUCCEEDED' && response.data.output) {
          const parsed = JSON.parse(response.data.output);
          pivotData = parsed; // Update with final data
          return parsed;
        }
        return null;
      }
    },
    options
  );
}

/**
 * Recursive parallel batch processing
 * Gọi API song song (maxConcurrency) và đệ quy cho đến khi xử lý hết
 */
export interface BatchProcessingOptions {
  /** Số lượng API call song song cùng lúc (default: 4) */
  maxConcurrency?: number;
  /** Batch size cho mỗi request (default: 10) */
  batchSize?: number;
}

export interface BatchProcessingCallbacks<T> {
  /** Function to fetch a batch */
  fetchBatch: (startIndex: number, batchSize: number) => Promise<any>;
  /** Process batch data */
  processBatch?: (batchData: T[]) => void | Promise<void>;
  /** Called when all batches are processed */
  onComplete: (allData?: T[]) => void;
  /** Called on error */
  onError?: (error: any) => void;
}

/**
 * Process batches recursively with parallel execution
 */
export async function processBatchesRecursively<T>(
  callbacks: BatchProcessingCallbacks<T>,
  options: BatchProcessingOptions = {},
  currentIndex: number = 0,
  accumulatedData: T[] = []
): Promise<void> {
  const { maxConcurrency = 4, batchSize = 10 } = options;

  try {
    // Fetch first batch to get total count
    if (currentIndex === 0) {
      const firstBatch = await callbacks.fetchBatch(0, batchSize);
      
      if (!firstBatch?.data?.HasMore) {
        // Only one batch
        if (callbacks.processBatch) {
          await callbacks.processBatch(firstBatch.data.Files);
        }
        callbacks.onComplete(firstBatch.data.Files);
        return;
      }

      const totalFiles = firstBatch.data.TotalFiles;
      const totalBatches = Math.ceil(totalFiles / batchSize);

      // Process remaining batches recursively in parallel
      await processNextBatches(
        callbacks,
        options,
        batchSize,
        totalBatches,
        [firstBatch.data.Files]
      );
      return;
    }

  } catch (error) {
    console.error('Error in processBatchesRecursively:', error);
    if (callbacks.onError) {
      callbacks.onError(error);
    }
  }
}

/**
 * Process next set of batches in parallel
 */
async function processNextBatches<T>(
  callbacks: BatchProcessingCallbacks<T>,
  options: BatchProcessingOptions,
  batchSize: number,
  totalBatches: number,
  accumulatedData: T[][] = [],
  currentBatch: number = 1
): Promise<void> {
  const { maxConcurrency = 4 } = options;

  if (currentBatch >= totalBatches) {
    // All batches processed
    const allData = accumulatedData.flat();
    callbacks.onComplete(allData);
    return;
  }

  // Calculate number of parallel calls
  const remainingBatches = totalBatches - currentBatch;
  const parallelCalls = Math.min(maxConcurrency, remainingBatches);

  // Create parallel promises
  const promises = [];
  for (let i = 0; i < parallelCalls; i++) {
    const batchIndex = currentBatch + i;
    const startIndex = batchIndex * batchSize;
    promises.push(callbacks.fetchBatch(startIndex, batchSize));
  }

  try {
    // Wait for all parallel calls
    const results = await Promise.all(promises);
    
    // Process each batch data
    for (const result of results) {
      if (result?.data?.Files) {
        accumulatedData.push(result.data.Files);
        if (callbacks.processBatch) {
          await callbacks.processBatch(result.data.Files);
        }
      }
    }

    // Recursively process next set of batches
    await processNextBatches(
      callbacks,
      options,
      batchSize,
      totalBatches,
      accumulatedData,
      currentBatch + parallelCalls
    );

  } catch (error) {
    console.error('Error in parallel batch processing:', error);
    if (callbacks.onError) {
      callbacks.onError(error);
    }
  }
}


// --- VÍ DỤ SỬ DỤNG ---

// import { StepFunctionService } from 'path-to-step-function-service';
// import { pollStepFunctionStatus } from 'path-to-polling-util';

// const stepFunctionService = new StepFunctionService();
// const executionArn = 'your-step-function-execution-arn';

// pollStepFunctionStatus(
//   stepFunctionService,
//   executionArn,
//   {
//     onSuccess: (data) => {
//       console.log('Step Function succeeded with data:', data);
//     },
//     onError: (error) => {
//       console.error('Step Function polling error:', error);
//     }
//   },
//   {
//     initialDelay: 1000,
//     subsequentDelay: 3000,
//     maxAttempts: 20
//   }
// );       


